import type {
  GetWorkspacePreflightStatusResponse,
  PreflightCheck,
} from '@sunstone/api-client/gen/deployments/v1/deployments_pb';
import {
  PreflightOutcome,
  PreflightRunStatus,
  PreflightSeverity,
  PreflightStatus,
} from '@sunstone/api-client/gen/deployments/v1/deployments_pb';
import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@sunstone/ui';
import { AlertTriangle, CheckCircle2, CircleHelp, Loader2, RefreshCw, XCircle } from 'lucide-react';
import { useState } from 'react';

interface PreflightChecklistDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The polled run status + report. Undefined before the first poll returns.
   * status === RUNNING → spinner; FAILED → error; COMPLETED → checks + outcome.
   *
   * Typed structurally (the common report fields) so both the workspace and the
   * ad-hoc status responses — distinct generated messages with identical fields —
   * can be passed.
   */
  status?: Pick<
    GetWorkspacePreflightStatusResponse,
    'status' | 'checks' | 'outcome' | 'blockers' | 'unknowns' | 'error'
  >;
  /** True while a poll request is in flight and we have no status yet. */
  isLoading?: boolean;
  /** Re-run the checks (restarts the workflow). */
  onRerun: () => void;
  /**
   * Proceed with the install. acknowledgeUnknowns is true when the operator
   * chose "Continue anyway" on a NEEDS_DECISION outcome; the caller threads it
   * into CreateWorkspace as acknowledge_unknowns.
   */
  onProceed: (acknowledgeUnknowns: boolean) => void;
  /**
   * Preview mode: render the report read-only with no install actions in the
   * footer (used by the standalone "Run preflight" button, which has nothing to
   * install). Only the Re-run button is shown.
   */
  previewOnly?: boolean;
}

// statusIcon renders the per-row icon for a check's status/severity.
function statusIcon(check: PreflightCheck) {
  switch (check.status) {
    case PreflightStatus.PASS:
      return <CheckCircle2 className="h-4 w-4 text-success shrink-0" />;
    case PreflightStatus.FAIL:
      return check.severity === PreflightSeverity.BLOCKER ? (
        <XCircle className="h-4 w-4 text-destructive shrink-0" />
      ) : (
        <AlertTriangle className="h-4 w-4 text-warning shrink-0" />
      );
    case PreflightStatus.UNKNOWN:
      return <CircleHelp className="h-4 w-4 text-muted-foreground shrink-0" />;
    default:
      return <CircleHelp className="h-4 w-4 text-muted-foreground shrink-0" />;
  }
}

// groupByCategory groups checks for display.
function groupByCategory(checks: PreflightCheck[]): Record<string, PreflightCheck[]> {
  return checks.reduce<Record<string, PreflightCheck[]>>((acc, c) => {
    const key = c.category || 'Other';
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(c);
    return acc;
  }, {});
}

export function PreflightChecklistDialog({
  open,
  onOpenChange,
  status,
  isLoading = false,
  onRerun,
  onProceed,
  previewOnly = false,
}: PreflightChecklistDialogProps) {
  // Show the "Required in:" provenance line per check; on by default, toggleable.
  const [showSource, setShowSource] = useState(true);
  // Run lifecycle. Treat "no status yet" or an explicit RUNNING as running.
  const runStatus = status?.status ?? PreflightRunStatus.UNSPECIFIED;
  const running =
    isLoading ||
    runStatus === PreflightRunStatus.RUNNING ||
    runStatus === PreflightRunStatus.UNSPECIFIED;
  const failed = runStatus === PreflightRunStatus.FAILED;
  const completed = runStatus === PreflightRunStatus.COMPLETED;

  // Gate outcome (only meaningful once COMPLETED).
  const outcome = status?.outcome ?? PreflightOutcome.UNSPECIFIED;
  const blocked = completed && outcome === PreflightOutcome.BLOCKED;
  const needsDecision = completed && outcome === PreflightOutcome.NEEDS_DECISION;
  const clear = completed && outcome === PreflightOutcome.CLEAR;
  const grouped = completed ? groupByCategory(status?.checks ?? []) : {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">Preflight checks</DialogTitle>
          <DialogDescription>
            We scan the installer and verify its requirements before starting the install.
            {blocked && ' One or more blockers must be resolved before you can continue.'}
            {needsDecision &&
              ' Some requirements could not be verified. Review them and decide whether to continue.'}
            {clear && ' Everything checks out.'}
          </DialogDescription>
        </DialogHeader>

        {running ? (
          <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            Running checks…
          </div>
        ) : failed ? (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {status?.error || 'The preflight run failed. Try running it again.'}
            </AlertDescription>
          </Alert>
        ) : completed ? (
          <div className="space-y-4 max-h-96 overflow-y-auto">
            <div className="flex justify-end">
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={showSource}
                  onChange={(e) => setShowSource(e.target.checked)}
                />
                Show source
              </label>
            </div>
            {Object.entries(grouped).map(([category, checks]) => (
              <div key={category} className="space-y-2">
                <h4 className="text-sm font-semibold text-foreground">{category}</h4>
                <ul className="space-y-2">
                  {checks.map((c) => (
                    <li key={c.id} className="flex items-start gap-2 text-sm">
                      {statusIcon(c)}
                      <div className="min-w-0">
                        <span className="font-medium text-foreground">
                          {c.type ? `${c.type}:${c.title}` : c.title}
                        </span>
                        {c.detail && <p className="text-xs text-muted-foreground">{c.detail}</p>}
                        {c.items.length > 0 && (
                          <ul className="ml-3 list-disc list-inside text-xs text-muted-foreground">
                            {c.items.map((item) => (
                              <li key={item}>{item}</li>
                            ))}
                          </ul>
                        )}
                        {c.remediation && c.status !== PreflightStatus.PASS && (
                          <p className="text-xs text-muted-foreground italic">{c.remediation}</p>
                        )}
                        {showSource && c.source && (
                          <p className="text-xs text-muted-foreground">Required in: {c.source}</p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            {(status?.checks.length ?? 0) === 0 && (
              <p className="text-sm text-muted-foreground">No requirements were found to check.</p>
            )}
          </div>
        ) : null}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button type="button" variant="outline" onClick={onRerun} disabled={running}>
            {running ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Re-run checks
          </Button>

          {!previewOnly && blocked && (
            <Button type="button" variant="destructive" disabled>
              {status?.blockers} blocker{status && status.blockers === 1 ? '' : 's'} — cannot start
            </Button>
          )}

          {!previewOnly && needsDecision && (
            <Button
              type="button"
              variant="default"
              onClick={() => onProceed(true)}
              disabled={running}
            >
              Continue anyway ({status?.unknowns} unverified)
            </Button>
          )}

          {!previewOnly && clear && (
            <Button
              type="button"
              variant="default"
              onClick={() => onProceed(false)}
              disabled={running}
            >
              Start installation
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
